import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { Header, Footer } from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "sonner";
import { AnalyticsTracker } from "@/components/Analytics";
import HomePage from "@/pages/HomePage";
import BushtechOverviewPage from "@/pages/BushtechOverviewPage";
import BushtechCanopiesHomePage from "@/pages/BushtechCanopiesHomePage";
import WellsideCanopiesPage from "@/pages/WellsideCanopiesPage";
import WellsideDetailPage from "@/pages/WellsideDetailPage";
import XSeriesTrayPage from "@/pages/XSeriesTrayPage";
import XSeriesDetailPage from "@/pages/XSeriesDetailPage";
import BushCabinPage from "@/pages/BushCabinPage";
import BushCabinDetailPage from "@/pages/BushCabinDetailPage";
import BushCabinCheaperComparisonPage from "@/pages/BushCabinCheaperComparisonPage";
import RoofRacksPage from "@/pages/RoofRacksPage";
import RoofRackDetailPage from "@/pages/RoofRackDetailPage";
import RoofTentsPage from "@/pages/RoofTentsPage";
import RoofTentDetailPage from "@/pages/RoofTentDetailPage";
import HardLidPage from "@/pages/HardLidPage";
import HardLidDetailPage from "@/pages/HardLidDetailPage";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import GroundTentsPage from "@/pages/GroundTentsPage";
import GroundTentDetailPage from "@/pages/GroundTentDetailPage";
import DrawersPage from "@/pages/DrawersPage";
import DrawerDetailPage from "@/pages/DrawerDetailPage";
import FridgesPage from "@/pages/FridgesPage";
import FridgeDetailPage from "@/pages/FridgeDetailPage";
import AwningsPage from "@/pages/AwningsPage";
import AwningDetailPage from "@/pages/AwningDetailPage";
import BullBarsPage from "@/pages/BullBarsPage";
import BullBarDetailPage from "@/pages/BullBarDetailPage";
import JimnyStorePage from "@/pages/JimnyStorePage";
import JimnyDetailPage from "@/pages/JimnyDetailPage";
import CookingPage from "@/pages/CookingPage";
import CookingDetailPage from "@/pages/CookingDetailPage";
import CampingAccessoriesPage from "@/pages/CampingAccessoriesPage";
import CampingAccessoryDetailPage from "@/pages/CampingAccessoryDetailPage";
import BushCabinAccessoryDetailPage from "@/pages/BushCabinAccessoryDetailPage";
import BuildYourBushCabinPage from "@/pages/BuildYourBushCabinPage";
import WellsideAccessoriesPage from "@/pages/WellsideAccessoriesPage";
import WellsideAccessoryDetailPage from "@/pages/WellsideAccessoryDetailPage";
import EnsuitesPage from "@/pages/EnsuitesPage";
import EnsuiteDetailPage from "@/pages/EnsuiteDetailPage";
import ClearancePage from "@/pages/ClearancePage";
import ClearanceDetailPage from "@/pages/ClearanceDetailPage";
import ExpeditionsPage from "@/pages/ExpeditionsPage";
import UteCamperBuyersGuidePage from "@/pages/UteCamperBuyersGuidePage";
import AboutPage from "@/pages/AboutPage";
import PolicyPage from "@/pages/PolicyPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import { AdminAuthProvider } from "@/admin/AdminAuthContext";
import AdminLoginPage from "@/admin/AdminLoginPage";
import AdminLayout from "@/admin/AdminLayout";
import AdminWellsideListPage from "@/admin/AdminWellsideListPage";
import AdminWellsideEditPage from "@/admin/AdminWellsideEditPage";
import AdminProductListPage from "@/admin/AdminProductListPage";
import AdminProductEditPage from "@/admin/AdminProductEditPage";
import AdminDashboardPage from "@/admin/AdminDashboardPage";
import AdminSettingsPage from "@/admin/AdminSettingsPage";
import AdminShippingRatesListPage from "@/admin/AdminShippingRatesListPage";
import AdminShippingRateEditPage from "@/admin/AdminShippingRateEditPage";
import AdminCategoryOrderPage from "@/admin/AdminCategoryOrderPage";
import AdminLegalPagesListPage from "@/admin/AdminLegalPagesListPage";
import AdminLegalPageEditPage from "@/admin/AdminLegalPageEditPage";
import AdminAnalyticsPage from "@/admin/AdminAnalyticsPage";
import AdminBushCabinBuildsPage from "@/admin/AdminBushCabinBuildsPage";
import AdminPostersPage from "@/admin/AdminPostersPage";
import NotFoundPage from "@/pages/NotFoundPage";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 96;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

const SiteChrome = ({ children }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isStandalone = pathname.startsWith("/bushtech-canopies");
  if (isAdmin || isStandalone) return children;
  return (
    <>
      <Header />
      <main className="pt-0">{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[var(--bg-main)] text-white">

        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <Toaster
            position="top-right"
            toastOptions={{
              classNames: {
                toast: "!bg-[#141414] !text-white !border !border-[#27272A]",
                description: "!text-[#A1A1AA]",
              },
            }}
          />
          <CartProvider>
          <AdminAuthProvider>
            <SiteChrome>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/bushtech" element={<BushtechOverviewPage />} />
                  <Route path="/bushtech-canopies" element={<BushtechCanopiesHomePage />} />
                  <Route path="/wellside-canopies" element={<WellsideCanopiesPage />} />
                  <Route path="/wellside-canopies/:slug" element={<WellsideDetailPage />} />
                  <Route path="/wellside-accessories" element={<WellsideAccessoriesPage />} />
                  <Route path="/wellside-accessories/:slug" element={<WellsideAccessoryDetailPage />} />
                  <Route path="/x-series-tray" element={<XSeriesTrayPage />} />
                  <Route path="/x-series-tray/:slug" element={<XSeriesDetailPage />} />
                  <Route path="/bush-cabin" element={<BushCabinPage />} />
                  <Route path="/bush-cabin/why-not-a-cheaper-camper" element={<BushCabinCheaperComparisonPage />} />
                  <Route path="/bush-cabin/:slug" element={<BushCabinDetailPage />} />
                  <Route path="/roof-racks" element={<RoofRacksPage />} />
                  <Route path="/roof-racks/:slug" element={<RoofRackDetailPage />} />
                  <Route path="/roof-tents" element={<RoofTentsPage />} />
                  <Route path="/roof-tents/:slug" element={<RoofTentDetailPage />} />
                  {FEATURE_FLAGS.HARD_LIDS_ENABLED && (
                    <>
                      <Route path="/hard-lids" element={<HardLidPage />} />
                      <Route path="/hard-lids/:slug" element={<HardLidDetailPage />} />
                    </>
                  )}
                  <Route path="/ground-tents" element={<GroundTentsPage />} />
                  <Route path="/ground-tents/:slug" element={<GroundTentDetailPage />} />
                  <Route path="/drawers-kitchens-tables" element={<DrawersPage />} />
                <Route path="/drawers-kitchens-tables/:slug" element={<DrawerDetailPage />} />
                <Route path="/12v-fridges" element={<FridgesPage />} />
                <Route path="/12v-fridges/:slug" element={<FridgeDetailPage />} />
                <Route path="/awnings" element={<AwningsPage />} />
                <Route path="/awnings/:slug" element={<AwningDetailPage />} />
                <Route path="/bull-bars" element={<BullBarsPage />} />
                <Route path="/bull-bars/:slug" element={<BullBarDetailPage />} />
                <Route path="/jimny-store" element={<JimnyStorePage />} />
                <Route path="/jimny-store/:slug" element={<JimnyDetailPage />} />
                <Route path="/camp-cooking" element={<CookingPage />} />
                <Route path="/camp-cooking/:slug" element={<CookingDetailPage />} />
                <Route path="/camping-accessories" element={<CampingAccessoriesPage />} />
                <Route path="/camping-accessories/:slug" element={<CampingAccessoryDetailPage />} />
                <Route path="/bush-cabin-accessories/:slug" element={<BushCabinAccessoryDetailPage />} />
                <Route path="/build-your-bush-cabin" element={<BuildYourBushCabinPage />} />
                <Route path="/ensuites" element={<EnsuitesPage />} />
                <Route path="/ensuites/:slug" element={<EnsuiteDetailPage />} />
                <Route path="/clearance" element={<ClearancePage />} />
                <Route path="/clearance/:slug" element={<ClearanceDetailPage />} />
                <Route path="/expeditions" element={<ExpeditionsPage />} />
                <Route path="/guides/ute-camper-buyers-guide" element={<UteCamperBuyersGuidePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy-policy" element={<PolicyPage slug="privacy-policy" />} />
                <Route path="/shipping-policy" element={<PolicyPage slug="shipping-policy" />} />
                <Route path="/returns-policy" element={<PolicyPage slug="returns-policy" />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="wellside-canopies" element={<AdminWellsideListPage />} />
                  <Route path="wellside-canopies/:slug" element={<AdminWellsideEditPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                  <Route path="shipping-rates" element={<AdminShippingRatesListPage />} />
                  <Route path="shipping-rates/:id" element={<AdminShippingRateEditPage />} />
                  <Route path="category-order" element={<AdminCategoryOrderPage />} />
                  <Route path="analytics" element={<AdminAnalyticsPage />} />
                  <Route path="bushcabin-builds" element={<AdminBushCabinBuildsPage />} />
                  <Route path="posters" element={<AdminPostersPage />} />
                  <Route path="legal-pages" element={<AdminLegalPagesListPage />} />
                  <Route path="legal-pages/:slug" element={<AdminLegalPageEditPage />} />
                  <Route path=":categorySlug" element={<AdminProductListPage />} />
                  <Route path=":categorySlug/:slug" element={<AdminProductEditPage />} />
                </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ErrorBoundary>
            </SiteChrome>
          </AdminAuthProvider>
          </CartProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
